import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { VOneIconComponent } from '@shared-components';
import { TooltipDirective } from '@shared-directives';
import { IconType } from '@shared-enums';
import { RegisteredArticleWarning } from '@shared-models';

import { InventoryWarningNotificationStoreService } from '../../services/inventory-warning-notification/inventory-warning-notification-store.service';

/**
 * Single, globally-mounted, non-invasive notification for unresolved
 * inventory warnings. Renders absolutely nothing when there are none.
 * Fixed to the bottom-right of the viewport — see
 * `docs/superpowers/specs/2026-08-03-inventory-warning-corner-notification-design.md`
 * for why this must be mounted at the true app root rather than inside any
 * dialog: a `position: fixed` element inside a Material dialog's animated
 * container can get trapped by the dialog's CSS `transform` instead of
 * pinning to the real viewport.
 */
@Component({
  selector: 'v-one-inventory-warning-notification',
  standalone: true,
  imports: [VOneIconComponent, TooltipDirective, TranslatePipe],
  templateUrl: './inventory-warning-notification.component.html',
  styleUrl: './inventory-warning-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryWarningNotificationComponent {
  private readonly store = inject(InventoryWarningNotificationStoreService);
  private readonly translate = inject(TranslateService);

  protected readonly iconType = IconType;

  readonly entries = this.store.entries;

  /**
   * One segment per affected article. The tooltip directive renders with
   * `white-space: normal`, so a `'\n'` join collapses into a run-on
   * sentence — ` | ` survives whitespace collapsing and stays readable in
   * the tooltip's narrow (228px) box.
   */
  readonly tooltipMessage = computed(() =>
    this.entries()
      .map((entry) => this.messageFor(entry))
      .join(' | '),
  );

  private messageFor(entry: RegisteredArticleWarning): string {
    const w = entry.warning;
    const docType = w.lastDocType
      ? this.translateDocType(w.lastDocType)
      : this.translate.instant(
          'inventoryArticleWarning.tooltip.unknownDocType',
        );

    const params = { code: entry.articleCode, count: w.failedCount, docType };

    if (w.lastFailedAt) {
      return this.translate.instant(
        'inventoryArticleWarning.tooltip.withDate',
        { ...params, date: this.formatDate(w.lastFailedAt) },
      );
    }

    return this.translate.instant(
      'inventoryArticleWarning.tooltip.withoutDate',
      params,
    );
  }

  /**
   * `ngx-translate`'s `instant()` returns the key itself when no
   * translation is registered for it, so an unmapped `docType` letter is
   * detected by comparing the result back to the key and falls back to the
   * raw letter.
   */
  private translateDocType(docType: string): string {
    const key = `inventoryArticleWarning.docType.${docType}`;
    const translated = this.translate.instant(key);
    return translated === key ? docType : translated;
  }

  /** Plain `YYYY-MM-DD` — same convention as the rest of this feature. */
  private formatDate(iso: string): string {
    return iso.split('T')[0] ?? iso;
  }
}
